import { ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    gridSubRange,
    VoxelSubRange,
} from '~/domain/services/imageService/gridGeometry';
import { PostProcessingMode } from '~/engine/util/algorithms/sdf/SDFPostProcessing';
import { ContourTexture } from '../../data/structure/ContourTexture';
import { Grid } from '../../data/common/Grid';
import { PlaneView } from '../views/PlaneView';
import {
    RectangleSelectViewLike,
    rectangleSelectAxis,
} from '../../tool/tools/contouring-tools/obliqueGating';

// ── Turning a viewport gesture into a delete box ─────────────────────
//
// Every deletion that starts on the image — the dragged rectangle and the
// single curve picked under the cursor — reaches
// `SDFOperations.applyClearPartsTouchingRect` as a {@link WorldBoundsMm}.
// Both are built here so the two cannot disagree about a series: they
// refuse through the same {@link rectangleSelectAxis} verdict, and both
// resolve against the target ROI's own texture grid, the grid
// `gridSubRange` will turn the box back into a voxel window with.

/**
 * How near a contour the pointer counts as being ON it, in millimetres.
 *
 * ONE radius for the label that names the structure and for the deletion
 * offered on it, so the distance at which a structure is named is the
 * distance at which pointing at it deletes.
 *
 * Stated in millimetres because the encoded field's byte scale differs
 * per texture (see `findRoiWithinMm`), so a radius in bytes would be a
 * different distance around every structure.
 */
export const CURVE_PICK_RADIUS_MM = 2.5;

/**
 * Drag under this many canvas pixels on either axis is an accidental
 * click, not a rectangle. Same guard {@link BoxZoomTool} applies (its
 * `boxWidth < 4 || boxHeight < 4`): without it, every stray click on the
 * viewport would run a full labeling pass and delete a part.
 */
export const MIN_DRAG_CANVAS_PIXELS = 4;

/**
 * Half-depth (mm) extruded along the slice normal in 3D mode — far enough
 * that no real volume's normal extent exceeds it. {@link gridSubRange}
 * clamps the resulting window to the ROI's grid, so deliberately
 * overshooting costs nothing.
 */
const VOLUME_HALF_DEPTH_MM = 100_000;

/**
 * The dragged rectangle as a voxel window, or `null` for a drag under
 * {@link MIN_DRAG_CANVAS_PIXELS} on either axis (an accidental click) or
 * on a view {@link rectangleSelectAxis} refuses.
 *
 * IN-PLANE the window comes from the drawn rectangle through
 * {@link gridSubRange}, which snaps outward — right here, because a voxel
 * the drag partly covers is inside what the user drew.
 *
 * ALONG THE SLICE NORMAL it does not, because the user drew nothing there.
 * A synthesised depth of one voxel about the view plane puts both faces
 * exactly on voxel boundaries (`snapToImagePixel` has already centred the
 * plane on the slice), and `gridSubRange`'s floor/ceil then admits the
 * neighbouring slice on a one-ulp error — deleting the structure's curve
 * on a slice the user cannot see. So the slice is resolved as an index and
 * the window is that one slice in 2D, or the whole grid in 3D.
 *
 * KNOWN LIMITATION: the in-plane snap still widens the window by up to one
 * voxel per axis beyond the mm-space rectangle, so what a part is tested
 * against is the drawn box, not exactly the pixels highlighted.
 */
export function rectangleToVoxelWindow(
    view: PlaneView,
    grid: Grid,
    start: ReadonlyVec2,
    end: ReadonlyVec2,
    mode: PostProcessingMode
): VoxelSubRange | null {
    const dragWidth = Math.abs(end[0] - start[0]);
    const dragHeight = Math.abs(end[1] - start[1]);
    if (
        dragWidth < MIN_DRAG_CANVAS_PIXELS ||
        dragHeight < MIN_DRAG_CANVAS_PIXELS
    ) {
        return null;
    }

    // Re-asked here rather than carried over from the press: the same
    // predicate decides both, so a view that has since been rotated
    // cannot release a box the press would no longer have started.
    const normalAxis = rectangleSelectAxis(view, grid);
    if (normalAxis === undefined) {
        return null;
    }

    // All four corners, not just the two extremes: the in-plane axes are
    // not the world axes on every view, so the box's world extent has to
    // come from the actual corner positions rather than an assumption
    // about which screen axis maps to which world axis.
    const corners = [
        view.patientCoordsFromCanvasCoords(start[0], start[1]),
        view.patientCoordsFromCanvasCoords(end[0], start[1]),
        view.patientCoordsFromCanvasCoords(start[0], end[1]),
        view.patientCoordsFromCanvasCoords(end[0], end[1]),
    ];

    const min = vec3.fromValues(Infinity, Infinity, Infinity);
    const max = vec3.fromValues(-Infinity, -Infinity, -Infinity);
    for (const corner of corners) {
        vec3.min(min, min, corner);
        vec3.max(max, max, corner);
    }

    // Extruded far enough to span the volume so the in-plane axes resolve
    // against a non-degenerate box; the normal axis is replaced below.
    min[normalAxis] -= VOLUME_HALF_DEPTH_MM;
    max[normalAxis] += VOLUME_HALF_DEPTH_MM;

    const range = gridSubRange(grid, {
        minX: min[0],
        maxX: max[0],
        minY: min[1],
        maxY: max[1],
        minZ: min[2],
        maxZ: max[2],
    });

    // The GRID axis the view looks along — not the patient axis, which the
    // window is not indexed by. rectangleSelectAxis has already established
    // that the two name the same axis of this grid.
    const gridAxis = grid.axisAlong(view.viewDirection);
    if (gridAxis === undefined) {
        return null;
    }

    if (mode === '3D') {
        return range;
    }

    // Resolved from the plane's own position rather than from a box face:
    // the plane sits at the slice CENTRE, which floors to its slice without
    // depending on which side of a boundary a rounding error falls.
    const centre = vec3.create();
    for (const corner of corners) {
        vec3.add(centre, centre, corner);
    }
    vec3.scale(centre, centre, 1 / corners.length);
    const slice = Math.floor(grid.toVoxelSpace(centre)[gridAxis]);
    const sizeAlong = [grid.sizeX, grid.sizeY, grid.sizeZ][gridAxis];
    if (slice < 0 || slice >= sizeAlong) {
        return null;
    }

    const axisKeys = [
        ['minX', 'maxX'],
        ['minY', 'maxY'],
        ['minZ', 'maxZ'],
    ] as const;
    const [minKey, maxKey] = axisKeys[gridAxis];
    return {
        ...range,
        [minKey]: slice,
        [maxKey]: slice + 1,
    };
}

/**
 * The window holding exactly grid voxel `(i, j, k)`.
 *
 * Stated in VOXELS, never as a world box for the clear to convert back.
 * A box whose faces sit exactly on voxel boundaries, converted through
 * float32 transforms and then rounded outward, widens by a whole voxel per
 * face on a one-ulp error — and the clear takes every part its window
 * touches, on every slice in it, so the pick would delete the curve running
 * alongside the one pointed at.
 */
function singleVoxelWindow(i: number, j: number, k: number): VoxelSubRange {
    return {
        minX: i,
        maxX: i + 1,
        minY: j,
        maxY: j + 1,
        minZ: k,
        maxZ: k + 1,
    };
}

/**
 * The voxel of the nearest contour within `radiusMm` of `(i, j)` on slice
 * `k`, or undefined when none is that near.
 *
 * IN-PLANE only. A curve is a slice-wise thing, and the clear floods
 * 4-connected within one slice, so a foreground voxel on a neighbouring
 * slice belongs to a curve the user is not pointing at.
 *
 * Nearest, not "any within the window": a window big enough to forgive a
 * near miss is also big enough to touch a second curve running alongside,
 * and clearing whichever the flood reached first would delete a curve the
 * user never pointed at. Taking the single closest voxel and flooding from
 * THAT resolves the miss without widening what can be caught.
 *
 * Ties go to the lowest row then the lowest column — arbitrary, but fixed,
 * so the same click always picks the same curve.
 */
function nearestContourVoxelInPlane(
    slice: Uint8Array,
    sizeX: number,
    sizeY: number,
    threshold: number,
    i: number,
    j: number,
    radiusX: number,
    radiusY: number
): { i: number; j: number } | undefined {
    let best: { i: number; j: number } | undefined;
    let bestDistanceSq = Infinity;
    const minJ = Math.max(0, j - radiusY);
    const maxJ = Math.min(sizeY - 1, j + radiusY);
    const minI = Math.max(0, i - radiusX);
    const maxI = Math.min(sizeX - 1, i + radiusX);
    for (let y = minJ; y <= maxJ; y++) {
        for (let x = minI; x <= maxI; x++) {
            if (slice[y * sizeX + x] < threshold) {
                continue;
            }
            // Measured in VOXELS on each axis scaled to the radius, so an
            // anisotropic grid does not reach further along its coarse
            // axis than the millimetre radius allows.
            const dx = (x - i) / radiusX;
            const dy = (y - j) / radiusY;
            const distanceSq = dx * dx + dy * dy;
            if (distanceSq > 1) {
                continue;
            }
            if (distanceSq < bestDistanceSq) {
                bestDistanceSq = distanceSq;
                best = { i: x, j: y };
            }
        }
    }
    return best;
}

/**
 * The world-space box of the voxel holding the contour the pointer is
 * picking out, or `undefined` on a view {@link rectangleSelectAxis}
 * refuses, a point outside the grid, or no contour within
 * {@link CURVE_PICK_RADIUS_MM}.
 *
 * This is the "delete the curve I am pointing at" box. One voxel is the
 * smallest window that can name a part, and `clearPartsTouchingWindow`
 * takes whichever part that voxel lands in WHOLE, so the window's size
 * decides only which curve is picked, never how much of it goes.
 *
 * The voxel is SNAPPED to the nearest contour within the radius rather
 * than being the one under the cursor. Pointing at a curve means landing
 * near it, not on it — a contour is a line about a voxel wide, and the
 * label that names the structure appears within this same radius of one.
 *
 * On the exact voxel the cursor is in, `gridSubRange` resolves a bare
 * point to one voxel everywhere EXCEPT on a voxel boundary, where its
 * floor and ceil agree and {@link subRangeIsEmpty} fires. Flooring to a
 * containing voxel first and handing back that voxel's own corners makes
 * the resolved window exactly the voxel picked, wherever in it the cursor
 * fell.
 */
export function voxelWindowAtPoint(
    view: RectangleSelectViewLike,
    texture: ContourTexture,
    pointMm: ReadonlyVec3,
    radiusMm: number = CURVE_PICK_RADIUS_MM
): VoxelSubRange | undefined {
    const grid = texture.textureGrid;
    if (rectangleSelectAxis(view, grid) === undefined) {
        return undefined;
    }

    const voxel = grid.toVoxelSpace(pointMm);
    if (!grid.includesVoxel(voxel)) {
        return undefined;
    }
    const i = Math.floor(voxel[0]);
    const j = Math.floor(voxel[1]);
    const k = Math.floor(voxel[2]);

    const sdf = texture.signedDistanceField;
    const slice = sdf?.pixels[k];
    if (!slice) {
        // No resident field to search. The containing voxel is the only
        // answer available, and the clear reports honestly if it holds
        // nothing.
        return singleVoxelWindow(i, j, k);
    }

    // The radius in voxels on each in-plane axis, so an anisotropic grid
    // reaches the same distance in millimetres either way. At least one,
    // or a grid coarser than the radius could search nothing at all.
    const radiusX = Math.max(1, Math.round(radiusMm / grid.resX));
    const radiusY = Math.max(1, Math.round(radiusMm / grid.resY));
    const nearest = nearestContourVoxelInPlane(
        slice,
        grid.sizeX,
        grid.sizeY,
        ContourTexture.SDF_THRESHOLD,
        i,
        j,
        radiusX,
        radiusY
    );
    if (!nearest) {
        return undefined;
    }
    return singleVoxelWindow(nearest.i, nearest.j, k);
}
